import React, { useState, useEffect } from "react";
import { Modal, Typography } from "antd";
import { CalendarOutlined } from "@ant-design/icons";
import { Button } from "@voicecare.ai/ui-library";
import { useTheme } from "../../../ThemeContext";
import { SlotSelectionPopup } from "./SlotSelectionPopup";
import moment from "moment";
import SimpleDatePicker from "../../common/SimpleDatePicker/SimpleDatePicker";

const { Text } = Typography;

export const DateSelectionPopup = ({
  visible,
  onClose,
  onDateSelect,
  callRequestId,
  onScheduleCall,
}) => {
  const { currentTheme, themeMode } = useTheme();
  const [showSlotSelection, setShowSlotSelection] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!visible) {
      setSelectedDate(null);
      setShowSlotSelection(false);
      setIsCalendarOpen(false);
    }
  }, [visible]);

  // Handle clicks outside calendar to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      const calendarContainer = document.querySelector('.date-selection-calendar-container');
      if (calendarContainer && !calendarContainer.contains(event.target)) {
        setIsCalendarOpen(false);
      }
    };

    if (isCalendarOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isCalendarOpen]);

  const handleDateChange = (date) => {
    if (date) {
      setSelectedDate(date);
      setIsCalendarOpen(false); // Close calendar when date is selected
    } else {
      setSelectedDate(null);
    }
  };

  const handleContinueToTimeSelection = () => {
    if (selectedDate) {
      setShowSlotSelection(true);
    }
  };

  const handleBackToDateSelection = () => {
    setShowSlotSelection(false);
    // Keep the selected date and ranges so user can see their selection
  };

  const handleSlotScheduleCall = (requestBody) => {
    // Pass the schedule call request to parent
    if (onScheduleCall) {
      onScheduleCall(requestBody);
    }
    handleClose();
  };

  const handleClose = () => {
    setSelectedDate(null);
    setShowSlotSelection(false);
    onClose();
  };

  // If showing slot selection, render the SlotSelectionPopup instead
  if (showSlotSelection) {
    return (
      <SlotSelectionPopup
        visible={visible}
        onClose={handleClose}
        callRequestId={callRequestId}
        onScheduleCall={handleSlotScheduleCall}
        selectedDate={
          selectedDate ? moment(selectedDate).format("MM/DD/YYYY") : null
        }
        onBackToDateSelection={handleBackToDateSelection}
      />
    );
  }

  return (
    <Modal
      title={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            height: "100%",
            marginLeft: "3.5rem",
          }}
        >
          <span
            style={{
              color: currentTheme.callhistory.text,
              fontSize: 22,
              fontWeight: 600,
            }}
          >
            Reschedule this call
          </span>
        </div>
      }
      open={visible}
      onCancel={handleClose}
      width={700}
      height={isCalendarOpen ? 650 : 350}
      footer={null}
      styles={{
        header: {
          backgroundColor: themeMode === "dark" ? "#04002b" : "#ffffff",
          // borderBottom: `1px solid ${themeMode === "dark" ? "#302d4c" : "#e9e8ea"}`,
          color: currentTheme.callhistory.text,
          padding: "24px 24px 0 24px",
        },
        body: {
          backgroundColor: themeMode === "dark" ? "#04002b" : "#ffffff",
          padding: "32px",
          minHeight: isCalendarOpen ? "550px" : "250px",
          transition: "min-height 0.3s ease-in-out",
        },
        content: {
          backgroundColor: themeMode === "dark" ? "#04002b" : "#ffffff",
          border: `1px solid ${themeMode === "dark" ? "#302d4c" : "#e9e8ea"}`,
          borderRadius: "16px",
          overflow: "hidden",
          boxShadow:
            themeMode === "dark"
              ? "0 20px 40px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.1)"
              : "0 20px 40px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)",
        },
        mask: {
          backgroundColor: "rgba(0, 0, 0, 0.6)",
        },
      }}
    >
      <div style={{ color: currentTheme.callhistory.text }}>
        {/* Instructions */}

        {/* Date Picker - Always Open and Visible */}
        <div
          style={{
            marginBottom: "24px",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              width: "500px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div
              className="date-selection-calendar-container"
              style={{
                position: "relative",
              }}
            >
              <div
                onClick={() => setIsCalendarOpen(true)}
                onFocus={() => setIsCalendarOpen(true)}
                style={{ position: "relative" }}
              >
                <SimpleDatePicker
                  selectedDate={selectedDate}
                  onChange={handleDateChange}
                  placeholder="Select Date"
                  className={themeMode}
                  dropdownPosition="bottom"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Selected Date Display */}
        {selectedDate && (
          <div
            style={{
              marginBottom: "24px",
              padding: "6px",
              backgroundColor:
                currentTheme.callhistory.bgLite ||
                currentTheme.callhistory.mainBackground,
              border: `1px solid ${currentTheme.callhistory.borderColor}`,
              borderRadius: "8px",
              textAlign: "center",
              margin: "auto",
              width: "29vw",
            }}
          >
            <Text
              style={{
                color: currentTheme.callhistory.text,
                fontSize: "14px",
                fontWeight: "bold",
              }}
            >
              Selected Date:{" "}
              {moment(selectedDate).format("dddd, MMMM DD, YYYY")}
            </Text>
            <div style={{ marginTop: "8px" }}>
              <Text
                style={{
                  color: currentTheme.callhistory.textLight,
                  fontSize: "12px",
                }}
              >
                Click "Continue to Time Selection" to proceed
              </Text>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "12px",
            alignItems: "center",
            marginTop: "32px",
            padding: "0 42px 8px 24px",
          }}
        >
          <Button
            label="Cancel"
            onClick={handleClose}
            style={{
              borderRadius: "8px",
              backgroundColor: themeMode === "dark" ? "#302d4c" : "#e9e8ea",
              color: currentTheme.homePage?.textColor || "#000",
              minWidth: "100px",
            }}
          />
          <Button
            label="Continue to Time Selection"
            onClick={handleContinueToTimeSelection}
            disabled={!selectedDate}
            style={{
              borderRadius: "8px",
              backgroundColor: "#e63e2b",
              color: "#fff",
              minWidth: "180px",
              opacity: !selectedDate ? 0.6 : 1,
              cursor: !selectedDate ? "not-allowed" : "pointer",
            }}
          />
        </div>
      </div>
    </Modal>
  );
};
